/**
 * @file `AtCapacityOverlay` — the full-screen "we're at capacity" wall shown
 *       when a learner's terminal is refused because every lab slot is taken
 *       (umbrella #1257 Part E, #1284).
 * @module @labcraft/lab/ui/components/at-capacity-overlay
 *
 * A doorway, not a slammed door. When the queue is full the learner can't get a
 * machine yet — but rather than a dead error, this overlay offers to enable a
 * "your spot is ready" desktop notification (Web Push) so the learner can leave
 * the tab and be called back the moment a slot opens. When push is dark or
 * unsupported, it degrades to a "keep this tab open" reassurance — the backend
 * admits an open tab automatically.
 *
 * The push opt-in is gated three ways: the daemon must have a VAPID keypair
 * (`GET /api/push/vapid` returns a non-empty `publicKey`), the browser must
 * support Web Push ({@link PushClient.isSupported}), and permission must not
 * already be granted. Clicking enable calls {@link PushClient.enable}; the
 * result flips the card to an `enabled` or `denied` message. The integration
 * layer renders this whenever the terminal WS closes with the AT_CAPACITY code
 * (I handle that wiring; this component is self-contained).
 */

import { useEffect, useState, type JSX } from 'react'
import type { PushVapidResponse } from '../../shared/routes.ts'
import { PushClient } from '../lib/push_client.ts'
import styles from './at_capacity_overlay.module.css'

/**
 * Props for {@link AtCapacityOverlay}.
 *
 * @property position - 0-based position in the capacity queue, if known. When a
 *   number, the card shows "You're #{position + 1} in line." Omitted when the
 *   backend didn't report a place (the headline still renders).
 */
export type AtCapacityOverlayProps = {
  position?: number
}

/**
 * Which face of the overlay is showing.
 *   - `prompt` — the default: capacity copy + (conditionally) the enable button.
 *   - `enabled` — push opt-in succeeded; the learner can leave the tab.
 *   - `denied` — push opt-in failed / was declined; keep the tab open.
 */
type Phase = 'prompt' | 'enabled' | 'denied'

/**
 * The at-capacity wall. See the file header for the opt-in gating.
 *
 * @param props - {@link AtCapacityOverlayProps}.
 * @returns The overlay element.
 */
export function AtCapacityOverlay(props: AtCapacityOverlayProps): JSX.Element {
  const [publicKey, setPublicKey] = useState<string>('')
  const [phase, setPhase] = useState<Phase>('prompt')

  // Fetch the VAPID public key on mount. An empty string (push dark) or a fetch
  // failure both leave `publicKey` empty, which hides the button and shows the
  // keep-tab-open fallback instead.
  useEffect(() => {
    let cancelled = false
    fetch('/api/push/vapid', { credentials: 'include' })
      .then(res => (res.ok ? (res.json() as Promise<PushVapidResponse>) : null))
      .then(data => {
        if (!cancelled && data) setPublicKey(data.publicKey)
      })
      .catch(() => {
        // Push simply stays unavailable — the fallback copy covers it.
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Whether to offer the enable button: a keypair exists, the browser supports
  // push, and permission isn't already granted. Notification may be undefined in
  // non-browser test environments, so guard the permission read.
  const permission =
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  const canOfferPush =
    publicKey !== '' && PushClient.isSupported() && permission !== 'granted'

  /** Run the push opt-in; flip the card to `enabled` or `denied` on the result. */
  async function onEnable(): Promise<void> {
    const ok = await PushClient.enable(publicKey)
    setPhase(ok ? 'enabled' : 'denied')
  }

  return (
    <div className={styles.scrim}>
      <div className={styles.card} role="status" aria-live="polite">
        {phase === 'prompt' && (
          <>
            <p className={styles.heading}>We&rsquo;re at capacity right now.</p>
            <p className={styles.body}>
              Every lab machine is in use at the moment. We&rsquo;ll get you in
              as soon as one frees up.
              {typeof props.position === 'number' && (
                <>
                  {' '}
                  You&rsquo;re{' '}
                  <b className={styles.position}>#{props.position + 1}</b> in
                  line.
                </>
              )}
            </p>
            {canOfferPush ? (
              <button
                type="button"
                className={styles.cta}
                onClick={() => void onEnable()}>
                Enable desktop notifications
              </button>
            ) : (
              <p className={styles.body}>
                Keep this tab open and we&rsquo;ll admit you automatically the
                moment a spot opens.
              </p>
            )}
          </>
        )}

        {phase === 'enabled' && (
          <>
            <p className={styles.heading}>Notifications on.</p>
            <p className={styles.body}>
              We&rsquo;ll ping you the moment your spot opens. You can leave
              this tab.
            </p>
          </>
        )}

        {phase === 'denied' && (
          <>
            <p className={styles.heading}>We&rsquo;re at capacity right now.</p>
            <p className={styles.body}>
              Notifications weren&rsquo;t enabled &mdash; keep this tab open and
              we&rsquo;ll admit you automatically when a spot opens.
            </p>
          </>
        )}
      </div>
    </div>
  )
}
